import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsManageOrganization from '../../../hooks/grid/gridOptions/useGridOptionsManageOrganization';
import commonColDef from '../../../data/grid/commonColDef';
import useColdefManageOrganization from '../../../hooks/grid/coldef/useColdefManageOrganization';
import ActionsManageOrganization from '../../cellRenderers/ActionsManageOrganization';
import OptionsTypeRenderer from '../../cellRenderers/OptionsTypeRenderer';
import UserNameFromEmailCellRenderer from '../../cellRenderers/UserNameFromEmailCellRenderer';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { setDialogMode, openDialog, selectOrganization } from '../../../redux/slices/organization';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import type { OrganizationType } from '../../../types/organization';

const OrganizationGrid = React.forwardRef<AgGridReactType>((props, ref): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentLang } = useLocales();
  const { isLoading, organizations } = useSelector((state: RootState) => state.organization);
  const { organizationBase, steelMaker, businessType, steelCategory } = useSelector((state: RootState) => state.options);
  const { users } = useSelector((state: RootState) => state.user);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | null>(null);
  const { gridOptions } = useGridOptionsManageOrganization();
  const customColDef: ColDef = {
    cellClass: 'cell',
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const columnDefs: ColDef[] = useColdefManageOrganization(t);
  const frameworkComponents = {
    actionsRenderer: ActionsManageOrganization,
    optionsTypeRenderer: OptionsTypeRenderer,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (organizations?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, organizations]);

  /**
   * カラム幅調整
   */
  useEffect(() => {
    if (gridApi && gridColumnApi && organizations) {
      const columnKeys: string[] = ['updatedAt', 'updatedBy', 'createdAt', 'createdBy'];
      gridColumnApi.autoSizeColumns(columnKeys);
      gridApi.redrawRows();
    }
  }, [gridColumnApi, organizations]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent): void => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする（ただし商社は編集できない）
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent): void => {
    const data: OrganizationType = event.data;
    // console.log(data);
    if (!data.isTradingCompany) {
      dispatch(selectOrganization(data));
      dispatch(setDialogMode('UPDATE'));
      dispatch(openDialog());
    } else {
      // do nothing
    }
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div className="ag-theme-balham" style={{ height: '70vh' }}>
        <AgGridReact
          ref={ref}
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={organizations}
          onGridReady={onGridReady}
          onRowDoubleClicked={onRowDoubleClicked}
          frameworkComponents={frameworkComponents}
          // filterValueGetterで使うデータ
          context={{
            currentLang,
            // reduxにあるものはコピーを渡す
            organizationBase: organizationBase?.map((base) => structuredClone(base)),
            steelMaker: steelMaker?.map((maker) => structuredClone(maker)),
            businessType: businessType?.map((business) => structuredClone(business)),
            steelCategory: steelCategory?.map((category) => structuredClone(category)),
            users: users?.map((user) => structuredClone(user)),
          }}
        />
      </div>
    </>
  );
});

// forwardRefの場合にはlinterに要求される場合がある
// https://stackoverflow.com/questions/67992894/component-definition-is-missing-display-name-for-forwardref
OrganizationGrid.displayName = 'OrganizationGrid';
export default OrganizationGrid;
